'use client'

import { usePathname } from 'next/navigation'
import { stripLocalePrefix } from '../../app/i18n/supported-locales'

/**
 * Renders `children` only while the browser is actually on `path`.
 *
 * WHY THIS IS NECESSARY — a genuine Next.js parallel-routes trap:
 * `@seo/default.jsx` only applies on a HARD navigation. On a SOFT (client-side) navigation to a
 * route the slot doesn't match, Next keeps rendering the slot's previously active state instead
 * of falling back to `default`. So clicking from /explore to the homepage left the entire
 * "AI Art Gallery" section sitting on `/` — wrong content, duplicated headings, and a nonsense
 * hierarchy. Hard navigations (and therefore curl, and page.goto in tests) never show it.
 *
 * The guard is client-side because that is where the staleness lives, but this still
 * server-renders: `children` is a server component passed through, and a client component's
 * first render happens on the server, where `usePathname()` already returns the real path. So
 * crawlers get the content in the initial HTML exactly as before.
 *
 * Locale prefixes are stripped so `/de/chat` matches `/chat`.
 */
export default function RouteScoped({ path, children }) {
  const pathname = usePathname()
  return stripLocalePrefix(pathname || '') === path ? children : null
}
